import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Gauge, Eye, ArrowClockwise, FloppyDisk, ArrowUUpLeft, CheckCircle,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { promptSuperPassword } from "@/components/admin/SuperPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin card for the Hybrid Ranking system.
 *
 * Operates the four backend endpoints:
 *   GET  /admin/ranking-mode
 *   POST /admin/ranking-mode
 *   POST /admin/rankings/preview
 *   POST /admin/rankings/apply
 *   POST /admin/rankings/rollback
 *
 * Workflow: toggle mode → Preview → Apply (snapshot is taken automatically)
 *           → optionally Rollback if the result is off. Toggle DOES NOT
 *           auto-recalculate so the admin can preview first.
 */
export default function HybridRankingCard() {
  const [mode, setMode] = useState(null); // "classic" | "hybrid"
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [preview, setPreview] = useState(null); // { mode, rows }
  const [lastBatch, setLastBatch] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/ranking-mode`, { withCredentials: true });
        setMode(r.data.mode);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load ranking mode");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isHybrid = mode === "hybrid";

  const handleToggle = async (checked) => {
    const next = checked ? "hybrid" : "classic";
    setSaving(true);
    try {
      await axios.post(`${API}/admin/ranking-mode`, { mode: next }, { withCredentials: true });
      setMode(next);
      setPreview(null);
      toast.success(
        `Switched to ${next} mode. Run Preview → Apply to recompute rankings.`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to switch mode");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const r = await axios.post(
        `${API}/admin/rankings/preview`,
        { mode },
        { withCredentials: true },
      );
      setPreview(r.data);
      toast.success(`Preview computed for ${r.data.count} players`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const handleApply = async () => {
    if (!window.confirm(
      `Apply the ${mode} ranking computation to all players?\n\n` +
      `A snapshot will be taken automatically so you can roll back.`,
    )) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/admin/rankings/apply`, {}, { withCredentials: true });
      setLastBatch(r.data.snapshot_batch_id);
      setPreview(null);
      toast.success(`Applied — ${r.data.count} players updated. Snapshot: ${r.data.snapshot_batch_id.slice(0, 8)}…`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const handleRollback = async () => {
    if (!window.confirm(
      "Roll back the most recent ranking recomputation?\n\n" +
      "This restores rank/tier/score fields on every player. " +
      "Placements, ELO and match points are never touched.",
    )) return;
    const pw = await promptSuperPassword({
      title: "Roll back rankings?",
      description: "Restores rank/tier/score fields on every player from the most recent snapshot.",
      actionLabel: "Rollback Rankings",
    });
    if (!pw) return;
    setRollingBack(true);
    try {
      const r = await axios.post(
        `${API}/admin/rankings/rollback`,
        lastBatch ? { snapshot_batch_id: lastBatch } : {},
        { headers: { "X-Super-Password": pw }, withCredentials: true },
      );
      toast.success(r.data.message);
      setLastBatch(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rollback failed");
    } finally {
      setRollingBack(false);
    }
  };

  const topTen = useMemo(() => (preview?.rows || []).slice(0, 10), [preview]);

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Loading ranking system…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border" data-testid="hybrid-ranking-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Gauge className="w-5 h-5 text-primary" weight="duotone" />
              Hybrid Ranking System
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Classic = placement points only (legacy). Hybrid = 75% placements (with time-decay) + 15% ELO + 10% recent form, with activity bonus and tier-buffer.
            </CardDescription>
          </div>
          <Badge
            className={isHybrid ? "bg-primary/20 text-primary border-primary/30" : "bg-muted text-muted-foreground"}
            data-testid="ranking-mode-badge"
          >
            {mode?.toUpperCase()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mode toggle */}
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted/20 px-4 py-3">
          <div>
            <Label className="text-sm font-medium">Use hybrid ranking</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Toggle only — does not auto-recalculate. Use Preview → Apply below.
            </p>
          </div>
          <Switch
            checked={isHybrid}
            onCheckedChange={handleToggle}
            disabled={saving}
            data-testid="toggle-hybrid-mode"
          />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm" variant="outline" onClick={handlePreview} disabled={previewing}
            className="border-primary/40 text-primary hover:bg-primary/10"
            data-testid="preview-rankings-btn"
          >
            <Eye className="w-4 h-4 mr-1.5" />
            {previewing ? "Computing…" : "Preview"}
          </Button>
          <Button
            size="sm" onClick={handleApply} disabled={applying}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="apply-rankings-btn"
          >
            <FloppyDisk className="w-4 h-4 mr-1.5" weight="fill" />
            {applying ? "Applying…" : "Apply & Snapshot"}
          </Button>
          <Button
            size="sm" variant="outline" onClick={handleRollback} disabled={rollingBack}
            className="border-destructive/40 text-destructive hover:bg-destructive/10 ml-auto"
            data-testid="rollback-rankings-btn"
          >
            <ArrowUUpLeft className="w-4 h-4 mr-1.5" />
            {rollingBack ? "Rolling back…" : lastBatch ? "Rollback last" : "Rollback latest"}
          </Button>
        </div>

        {/* Preview summary */}
        {preview && (
          <div className="border border-border rounded-lg" data-testid="preview-results">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/10">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <ArrowClockwise className="w-3.5 h-3.5" /> Preview — top 10 ({preview.mode})
              </span>
              <span className="text-[11px] text-muted-foreground">{preview.count} players</span>
            </div>
            <div className="divide-y divide-border">
              {topTen.map((r, i) => {
                const tierChanged = r.previous_tier && r.tier && r.previous_tier !== r.tier;
                const rankChanged = r.previous_ranking && r.previous_ranking !== r.ranking;
                return (
                  <div
                    key={r.id}
                    className="px-4 py-2 flex items-center gap-3 text-sm"
                    data-testid={`preview-row-${i + 1}`}
                  >
                    <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 ${
                      i === 0 ? "rank-1" : i === 1 ? "rank-2" : i === 2 ? "rank-3" : "bg-muted text-muted-foreground"
                    }`}>{r.ranking}</span>
                    <span className="flex-1 font-medium truncate">{r.name}</span>
                    <span className="text-[11px] text-muted-foreground tabular-nums w-14 text-right">
                      ELO {Math.round(r.elo_rating ?? 0)}
                    </span>
                    <span className="text-[11px] text-muted-foreground tabular-nums w-16 text-right">
                      {r.legacy_total_points} lgcy
                    </span>
                    <span className="font-heading font-bold text-primary tabular-nums w-16 text-right">
                      {r.ranking_score}
                    </span>
                    <Badge
                      className={`text-[10px] ${r.tier === "premier" ? "tier-premier" : "tier-challenger"}`}
                    >
                      {r.tier === "premier" ? "Premier" : "Challenger"}
                    </Badge>
                    {(tierChanged || rankChanged) && (
                      <CheckCircle className="w-3.5 h-3.5 text-secondary" weight="fill" title="Changed" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="rounded-lg bg-muted/10 border border-border px-3 py-2 text-[11px] text-muted-foreground">
          <p>
            <strong>Note:</strong> Placements, ELO and match points are NEVER modified by this tool.
            Only the derived fields (ranking, tier, ranking_score, seeding_score) on player docs are
            rewritten on Apply, and a rollback is always available. For a clean hybrid baseline, run
            the Premier ELO Rebaseline first so everyone starts from the same 1500.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
